import { t as rgbaToImageData } from "./rgba-image-data-_1qg09ps.js";
import { meanEdgePx } from "./ar/qr/qr-pose.js";
//#region ../src/ar/qr/qr-corner-order.ts
/**
* Canonical QR corner order (TL, TR, BR, BL of the SYMBOL) from the image,
* whatever order the detector reported (QR near-frontal pose plan
* 2026-09-23-2314, M1c; "Cause A" of 2026-06-20-0030). See
* qr-corner-order.ts.md.
*
* A QR symbol has a finder pattern at three of its corners (TL, TR, BL); the
* corner without one is BR. Each corner's diagonal is sampled inward and
* tested for the finder's dark-light-dark-light-dark run profile (1:1:3:1:1).
* Pure; no DOM.
*/
/** How far along each half-diagonal to sample (0 = corner, 1 = centre). */
const PROFILE_EXTENT = .8;
/** A run within these factors of its expected length still counts. */
const RING_TOLERANCE = [.5, 1.6];
const CORE_TOLERANCE = [2, 4.2];
/** Leading light samples tolerated before the first dark run (corner offset). */
const MAX_LEADING_LIGHT = .12;
const UNIT_CORNERS = [
	[0, 0],
	[1, 0],
	[1, 1],
	[0, 1]
];
/** Closed-form projective map of the unit square onto `q` (0,0)->q0 ... (0,1)->q3. */
function squareToQuad(q) {
	const [p0, p1, p2, p3] = q;
	const dx1 = p1.x - p2.x;
	const dx2 = p3.x - p2.x;
	const dx3 = p0.x - p1.x + p2.x - p3.x;
	const dy1 = p1.y - p2.y;
	const dy2 = p3.y - p2.y;
	const dy3 = p0.y - p1.y + p2.y - p3.y;
	let g = 0;
	let h = 0;
	if (Math.abs(dx3) > 1e-12 || Math.abs(dy3) > 1e-12) {
		const den = dx1 * dy2 - dx2 * dy1;
		g = (dx3 * dy2 - dx2 * dy3) / den;
		h = (dx1 * dy3 - dx3 * dy1) / den;
	}
	const a = p1.x - p0.x + g * p1.x;
	const b = p3.x - p0.x + h * p3.x;
	const d = p1.y - p0.y + g * p1.y;
	const e = p3.y - p0.y + h * p3.y;
	return (u, v) => {
		const w = g * u + h * v + 1;
		return {
			x: (a * u + b * v + p0.x) / w,
			y: (d * u + e * v + p0.y) / w
		};
	};
}
/** Bilinear luminance at a continuous pixel position; NaN outside the image. */
function luminanceAt(image, x, y) {
	const fx = x - .5;
	const fy = y - .5;
	const x0 = Math.floor(fx);
	const y0 = Math.floor(fy);
	if (x0 < 0 || y0 < 0 || x0 + 1 >= image.width || y0 + 1 >= image.height) return NaN;
	const tx = fx - x0;
	const ty = fy - y0;
	const lum = (i, j) => {
		const o = (j * image.width + i) * 4;
		const d = image.data;
		return .299 * d[o] + .587 * d[o + 1] + .114 * d[o + 2];
	};
	return (1 - ty) * ((1 - tx) * lum(x0, y0) + tx * lum(x0 + 1, y0)) + ty * ((1 - tx) * lum(x0, y0 + 1) + tx * lum(x0 + 1, y0 + 1));
}
/** Luminance along corner `k`'s half-diagonal, corner first; null if it leaves the image. */
function diagonalProfile(image, map, k) {
	const [cu, cv] = UNIT_CORNERS[k];
	const start = map(cu, cv);
	const endU = cu + (.5 - cu) * PROFILE_EXTENT;
	const endV = cv + (.5 - cv) * PROFILE_EXTENT;
	const far = map(endU, endV);
	const lengthPx = Math.hypot(far.x - start.x, far.y - start.y);
	const n = Math.max(40, Math.ceil(lengthPx * 3));
	const out = [];
	for (let i = 0; i < n; i++) {
		const s = i / (n - 1);
		const p = map(cu + (endU - cu) * s, cv + (endV - cv) * s);
		const l = luminanceAt(image, p.x, p.y);
		if (!Number.isFinite(l)) return null;
		out.push(l);
	}
	return out;
}
function darkFirstRuns(profile, threshold) {
	let i = 0;
	while (i < profile.length && profile[i] >= threshold) i++;
	if (i > profile.length * MAX_LEADING_LIGHT) return null;
	const lead = i;
	const runs = [];
	let dark = true;
	let len = 0;
	for (; i < profile.length; i++) {
		const isDark = profile[i] < threshold;
		if (isDark === dark) len++;
		else {
			runs.push(len);
			dark = isDark;
			len = 1;
		}
	}
	runs.push(len);
	return {
		lead,
		runs
	};
}
function within(value, expected, [lo, hi]) {
	return value >= expected * lo && value <= expected * hi;
}
/** Five consecutive runs in a finder's 1:1:3:1:1 proportion. */
function isFinderRatio(a, b, c, d, e) {
	const unit = (a + b + c + d + e) / 7;
	return within(a, unit, RING_TOLERANCE) && within(b, unit, RING_TOLERANCE) && within(c, unit * 3, [CORE_TOLERANCE[0] / 3, CORE_TOLERANCE[1] / 3]) && within(d, unit, RING_TOLERANCE) && within(e, unit, RING_TOLERANCE);
}
/** The first five runs look like a finder's diagonal: 1:1:3:1:1. */
function isFinderProfile(fr) {
	if (!fr || fr.runs.length < 6) return false;
	const [a, b, c, d, e] = fr.runs;
	return isFinderRatio(a, b, c, d, e);
}
/**
* Cross-check a finder candidate the way zxing does: scan through the centre
* of its core along both symbol axes; a real finder reads 1:1:3:1:1 centred
* there in every direction. The alignment pattern plus data modules near BR
* can mimic a finder along the DIAGONAL (milestone review 2026-09-24,
* finding 1), but not across it.
*/
function crossChecks(image, map, k, fr, diagonalSamples, threshold) {
	const [cu, cv] = UNIT_CORNERS[k];
	const [a, b, c, d, e] = fr.runs;
	const perSample = .5 * PROFILE_EXTENT / (diagonalSamples - 1);
	const coreMid = fr.lead + a + b + c / 2;
	const centreU = cu + Math.sign(.5 - cu) * coreMid * perSample;
	const centreV = cv + Math.sign(.5 - cv) * coreMid * perSample;
	const module = (a + b + c + d + e) * perSample / 7;
	return crossScanIsFinder(image, map, [centreU, centreV], [1, 0], module, threshold) && crossScanIsFinder(image, map, [centreU, centreV], [0, 1], module, threshold);
}
/** One cross-scan: 1:1:3:1:1 with the core run containing the centre. */
function crossScanIsFinder(image, map, centre, axis, module, threshold) {
	const half = 6 * module;
	const from = map(centre[0] - axis[0] * half, centre[1] - axis[1] * half);
	const to = map(centre[0] + axis[0] * half, centre[1] + axis[1] * half);
	const n = Math.max(40, Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) * 3)) | 1;
	const runs = [];
	for (let i = 0; i < n; i++) {
		const t = -half + 2 * half * i / (n - 1);
		const pt = map(centre[0] + axis[0] * t, centre[1] + axis[1] * t);
		const l = luminanceAt(image, pt.x, pt.y);
		if (!Number.isFinite(l)) return false;
		const dark = l < threshold;
		const last = runs[runs.length - 1];
		if (last && last.dark === dark) last.len++;
		else runs.push({
			dark,
			start: i,
			len: 1
		});
	}
	const mid = (n - 1) / 2;
	const at = runs.findIndex((r) => mid >= r.start && mid < r.start + r.len);
	if (at < 2 || at + 2 >= runs.length || !runs[at].dark) return false;
	const [l2, l1, core, r1, r2] = runs.slice(at - 2, at + 3).map((r) => r.len);
	return isFinderRatio(l2, l1, core, r1, r2);
}
/** Midpoint of the 10th and 90th percentiles of all samples. */
function darkLightThreshold(profiles) {
	const all = profiles.flat().sort((x, y) => x - y);
	const at = (p) => all[Math.min(all.length - 1, Math.floor(p * all.length))];
	return (at(.1) + at(.9)) / 2;
}
function rotated(q, k) {
	return [
		q[k % 4],
		q[(k + 1) % 4],
		q[(k + 2) % 4],
		q[(k + 3) % 4]
	];
}
/**
* Put the corners into symbol order (TL, TR, BR, BL) using the finder
* patterns. Only a cyclic rotation is ever applied; unless exactly one
* corner lacks a finder, the input comes back unchanged with
* `confident: false`.
*/
function canonicalizeCorners(image, corners) {
	const input = [...corners];
	const unsure = {
		corners: input,
		confident: false,
		source: "native"
	};
	if (corners.length !== 4 || image.width < 2 || image.height < 2) return unsure;
	const map = squareToQuad(input);
	const profiles = [];
	for (let k = 0; k < 4; k++) {
		const p = diagonalProfile(image, map, k);
		if (!p) return unsure;
		profiles.push(p);
	}
	const threshold = darkLightThreshold(profiles);
	const missing = profiles.map((p, k) => {
		const fr = darkFirstRuns(p, threshold);
		return isFinderProfile(fr) && crossChecks(image, map, k, fr, p.length, threshold);
	}).flatMap((f, k) => f ? [] : [k]);
	if (missing.length !== 1) return unsure;
	return {
		corners: rotated(input, (missing[0] + 2) % 4),
		confident: true,
		source: "finder"
	};
}
function centroid(q) {
	return {
		x: (q[0].x + q[1].x + q[2].x + q[3].x) / 4,
		y: (q[0].y + q[1].y + q[2].y + q[3].y) / 4
	};
}
/**
* The roll (deg, in (-180, 180]) that best maps the centred quad `from`
* onto the centred quad `to`, corner i to corner i (a least-squares fit of
* the rotation angle). It ignores translation, so a pan never reads as a
* roll.
*/
function quadRollDeg(from, to) {
	const a = centroid(from);
	const b = centroid(to);
	let cross = 0;
	let dot = 0;
	for (let i = 0; i < 4; i++) {
		const u = {
			x: from[i].x - a.x,
			y: from[i].y - a.y
		};
		const v = {
			x: to[i].x - b.x,
			y: to[i].y - b.y
		};
		cross += u.x * v.y - u.y * v.x;
		dot += u.x * v.x + u.y * v.y;
	}
	return Math.atan2(cross, dot) * 180 / Math.PI;
}
/**
* What the chain would make of `corners` given the last known order: the
* cyclic shift whose roll from it is smallest - if that roll is within
* `maxRollDeg` and the centre did not jump - else null (the chain ends).
*/
function chainPick(last, corners, maxRollDeg, maxJumpEdges) {
	const edge = meanEdgePx(last.corners) ?? NaN;
	const from = centroid(last.corners);
	const to = centroid(corners);
	if (!(Math.hypot(to.x - from.x, to.y - from.y) <= maxJumpEdges * edge)) return null;
	let best = null;
	let bestRoll = Infinity;
	for (let k = 0; k < 4; k++) {
		const candidate = rotated(corners, k);
		const roll = Math.abs(quadRollDeg(last.corners, candidate));
		if (roll < bestRoll) {
			bestRoll = roll;
			best = candidate;
		}
	}
	return bestRoll <= maxRollDeg ? best : null;
}
function sameOrder(a, b) {
	return a.every((p, i) => p.x === b[i].x && p.y === b[i].y);
}
/**
* {@link canonicalizeCorners} plus a per-code CHAIN (near-frontal pose plan
* §42): an unsure frame takes the cyclic shift of its corners with the
* smallest roll from the code's last known order (a finder frame or an
* earlier chained one) and becomes the new memory - while the previous
* detection is under `memoryMs` old, on the same capture size, the roll is
* within `maxRollDeg` and the centre did not jump. Otherwise the chain ends
* and the frame keeps the detector's order. A finder frame always
* re-anchors the chain; after a chained frame it reports whether the chain
* would have agreed (`audit`). Non-finite or non-positive options fall
* back to the defaults.
*/
function createCornerOrderCanonicalizer(options = {}) {
	const now = options.now ?? (() => performance.now());
	const memoryMs = positiveOr(options.memoryMs, 500);
	const maxRollDeg = positiveOr(options.maxRollDeg, 30);
	const maxJumpEdges = positiveOr(options.maxJumpEdges, 1.5);
	const orderFrame = options.orderFrame ?? canonicalizeCorners;
	const memory = /* @__PURE__ */ new Map();
	/** The live chain of `text` for this frame, pruning stale entries. */
	function liveChain(text, image, t) {
		for (const [key, entry] of memory) if (t - entry.at > memoryMs) memory.delete(key);
		const last = memory.get(text);
		if (!last) return null;
		return last.width === image.width && last.height === image.height ? last : null;
	}
	return { canonicalize(text, image, corners) {
		const result = orderFrame(image, corners);
		const t = now();
		const last = corners.length === 4 ? liveChain(text, image, t) : null;
		const remember = (q, chained) => memory.set(text, {
			corners: q,
			chained,
			at: t,
			width: image.width,
			height: image.height
		});
		if (result.confident) {
			const audit = last?.chained ? auditOf(chainPick(last, [...corners], maxRollDeg, maxJumpEdges), result.corners) : void 0;
			remember(result.corners, false);
			return audit ? {
				...result,
				audit
			} : result;
		}
		const pick = last ? chainPick(last, result.corners, maxRollDeg, maxJumpEdges) : null;
		if (!pick) {
			memory.delete(text);
			return result;
		}
		remember(pick, true);
		return {
			corners: pick,
			confident: false,
			source: "memory"
		};
	} };
}
/** A finite, positive option, else the default (NaN compares false everywhere). */
function positiveOr(value, fallback) {
	return value !== void 0 && Number.isFinite(value) && value > 0 ? value : fallback;
}
function auditOf(pick, finder) {
	if (!pick) return "reject";
	return sameOrder(pick, finder) ? "agree" : "disagree";
}
//#endregion
//#region ../src/ar/qr/qr-frontend.ts
/**
* Default conversion: wrap the frame in `ImageData` WITHOUT copying (the frame
* is already an owned copy; QR perf plan 2026-09-23, M3). The adopt-or-copy
* rule lives in one place, `../rgba-image-data.ts`.
*/
const defaultToImageData = rgbaToImageData;
var BarcodeDetectorFrontEnd = class {
	kind = "barcode-detector";
	detector;
	toSource;
	orderCorners;
	constructor(detector, toSource = defaultToImageData, orderCorners = defaultCornerOrderer()) {
		this.detector = detector;
		this.toSource = toSource;
		this.orderCorners = orderCorners;
	}
	async detect(image) {
		const results = await this.detector.detect(this.toSource(image));
		for (const r of results) {
			const corners = toQuad(r.cornerPoints);
			if (corners && typeof r.rawValue === "string" && r.rawValue.length > 0) {
				const ordered = this.orderCorners(r.rawValue, image, corners);
				return {
					corners: ordered.corners,
					text: r.rawValue,
					orderSource: ordered.source,
					...ordered.audit ? { orderAudit: ordered.audit } : {}
				};
			}
		}
		return null;
	}
};
/**
* Build a {@link BarcodeDetectorFrontEnd} if the runtime exposes a
* `BarcodeDetector` constructor; otherwise `null` (→ the caller must handle the
* unsupported-browser case; there is no OpenCV fallback). `ctor` is injectable
* for tests; `toSource` overrides the default no-copy conversion (e.g. a
* measuring or copying one for a performance A/B).
*/
function createBarcodeDetectorFrontEnd(ctor, toSource) {
	const Ctor = ctor ?? globalThis.BarcodeDetector;
	if (!Ctor) return null;
	return new BarcodeDetectorFrontEnd(new Ctor({ formats: ["qr_code"] }), toSource ?? defaultToImageData);
}
/** The finder-pattern orderer with its per-code memory, one per front end. */
function defaultCornerOrderer() {
	const canonicalizer = createCornerOrderCanonicalizer();
	return (text, image, corners) => {
		const result = canonicalizer.canonicalize(text, image, corners);
		return {
			corners: result.corners,
			source: result.source,
			...result.audit ? { audit: result.audit } : {}
		};
	};
}
function toQuad(points) {
	if (points.length !== 4) return null;
	const [a, b, c, d] = points;
	if (!a || !b || !c || !d) return null;
	const out = [
		{
			x: a.x,
			y: a.y
		},
		{
			x: b.x,
			y: b.y
		},
		{
			x: c.x,
			y: c.y
		},
		{
			x: d.x,
			y: d.y
		}
	];
	if (out.some((p) => !Number.isFinite(p.x) || !Number.isFinite(p.y))) return null;
	return out;
}
//#endregion
export { createBarcodeDetectorFrontEnd as n, BarcodeDetectorFrontEnd as t };
